import React, { useState, useEffect } from 'react';
import { Autocomplete, TextField, Chip, CircularProgress } from '@mui/material';
import { transshipPointsService } from 'services/transshipPointsService';
import { StyledContainer } from './styles';

interface TransshipPoint {
  id: number;
  name: string;
  code?: string;
  agileCode?: string;
}

export interface TpMultiSelectAutocompleteProps {
  /** Valores selecionados (IDs) */
  value?: number[];
  /** Função chamada quando os valores mudam */
  onChange: (value: number[]) => void;
  /** Label do campo */
  label?: string;
  /** Se o componente está desabilitado */
  disabled?: boolean;
  /** Placeholder quando nenhum item está selecionado */
  placeholder?: string;
  /** Loading externo */
  loading?: boolean;
}

export const TpMultiSelectAutocomplete = ({
  value = [],
  onChange,
  label = "Pontos de transbordo",
  disabled = false,
  placeholder = "Selecione os pontos de transbordo",
  loading: externalLoading = false
}: TpMultiSelectAutocompleteProps) => {
  const [options, setOptions] = useState<TransshipPoint[]>([]);
  const [internalLoading, setInternalLoading] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<TransshipPoint[]>([]);

  // Busca os transship points da API
  useEffect(() => {
    const fetchTransshipPoints = async () => {
      try {
        setInternalLoading(true);
        const response = await transshipPointsService.getTransshipPoints({
          pageSize: 1000,
          inUse: 1
        });

        if (response && response?.results) {
          setOptions(response?.results);
        }
      } catch (error) {
        console.error('Erro ao buscar pontos de transbordo:', error);
      } finally {
        setInternalLoading(false);
      }
    };

    fetchTransshipPoints();
  }, []);

  // Atualiza os valores selecionados quando o prop value muda
  useEffect(() => {
    if (value && value.length > 0 && options.length > 0) {
      const selected = options.filter(option => value.includes(option.id));
      setSelectedOptions(selected);
    } else {
      setSelectedOptions([]);
    }
  }, [value, options]);

  const handleChange = (_: any, newValue: TransshipPoint[]) => {
    setSelectedOptions(newValue);
    const ids = newValue.map(option => option.id);
    onChange(ids);
  };

  const getOptionLabel = (option: TransshipPoint) => {
    const name = option.name?.trim();
    if (name) return name;
    const code = option.agileCode ?? option.code;
    return code ? String(code) : String(option.id);
  };

  const isLoading = internalLoading || externalLoading;

  return (
    <StyledContainer>
      <Autocomplete
        multiple
        options={options}
        value={selectedOptions}
        onChange={handleChange}
        getOptionLabel={getOptionLabel}
        disabled={disabled || isLoading}
        loading={isLoading}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option.id}
              label={getOptionLabel(option)}
              size="small"
              sx={{
                backgroundColor: '#89BD23',
                color: 'white',
                '& .MuiChip-deleteIcon': {
                  color: 'white',
                  '&:hover': {
                    color: '#f0f0f0'
                  }
                }
              }}
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={selectedOptions.length === 0 ? placeholder : ''}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading ? <CircularProgress color="inherit" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        filterSelectedOptions
        sx={{
          '& .MuiOutlinedInput-root': {
            padding: '8px 12px',
          }
        }}
      />
    </StyledContainer>
  );
};
